import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../AuthContext';
import { C, S, getStatusBadgeStyle, formatDate, formatCurrency } from '../styles';
import { DropZone } from '../components/DropZone';
import { sendEmail } from '../utils/emailService';
import { varRaisedNotification } from '../utils/emailTemplates';

const today = new Date().toISOString().split('T')[0];

const DEFAULT_COVERING_NOTE =
  `We submit our Variation Quotation for the above instruction.\n` +
  `The works described are considered to be outside the subcontract scope for the reasons stated in our instruction reference.\n` +
  `We request your written acceptance of this quotation in accordance with the subcontract conditions.\n\n` +
  `This Variation Quotation is submitted in accordance with the subcontract conditions. The works described constitute a variation to the subcontract works and are outside the subcontract scope for the reasons stated. Proline Internals Ltd reserves the right to recover all reasonable costs and any extension of time to which it may be entitled.`;

const VALUATION_OPTIONS = [
  { value: 'contract_rates', label: 'Contract Rates', desc: 'Using agreed schedule of rates' },
  { value: 'new_rates', label: 'New Rates', desc: 'New rates agreed or to be agreed' },
  { value: 'fair_valuation', label: 'Fair Valuation', desc: 'Fair and reasonable basis' },
  { value: 'dayworks', label: 'Dayworks', desc: 'Valued from signed daywork sheets' },
  { value: 'mixed', label: 'Mixed', desc: 'Combination of methods' },
];

const RISK_OPTIONS = [
  { value: 'low', label: 'Low', desc: 'Instruction clear, rates agreed', color: C.success, bg: '#f0fdf4', border: '#bbf7d0' },
  { value: 'medium', label: 'Medium', desc: 'Rates to be agreed, verbal instruction', color: C.warning, bg: '#fffbeb', border: '#fde68a' },
  { value: 'high', label: 'High', desc: 'Disputed entitlement, no instruction', color: C.danger, bg: '#fef2f2', border: '#fecaca' },
];

function ProofDot({ status }) {
  const color = status === 'confirmed' ? C.success : status === 'pending' ? '#d1d5db' : C.danger;
  const icon = status === 'confirmed' ? '✓' : status === 'pending' ? '○' : '✗';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: '22px', height: '22px', borderRadius: '50%',
      background: color, color: '#fff', fontSize: '11px', fontWeight: '700',
    }}>{icon}</span>
  );
}

export default function VariationForm({ navigate, params }) {
  const { profile } = useAuth();
  const role = profile?.role || '';
  const isAllowed = role === 'proline_qs' || role === 'admin';
  const isSiteManagerFlow = role === 'proline_sm' && !!params?.instructionId;

  const projectId = params?.projectId || '';

  const [instructions, setInstructions] = useState([]);
  const [linkedDayworks, setLinkedDayworks] = useState([]);
  const [selectedInstruction, setSelectedInstruction] = useState(null);
  const [generatedRef, setGeneratedRef] = useState('');

  const [instructionId, setInstructionId] = useState(params?.instructionId || '');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [scopeNotes, setScopeNotes] = useState('');
  const [valuationBasis, setValuationBasis] = useState('contract_rates');
  const [programmeImpact, setProgrammeImpact] = useState(false);
  const [programmeImpactDays, setProgrammeImpactDays] = useState(0);
  const [riskLevel, setRiskLevel] = useState('low');
  const [value, setValue] = useState('');
  const [vqSubmittedDate, setVqSubmittedDate] = useState(today);
  const [notesForMc, setNotesForMc] = useState(DEFAULT_COVERING_NOTE);
  const [notificationDate, setNotificationDate] = useState('');

  const [instrAttachments, setInstrAttachments] = useState([]);
  const [siteNotes, setSiteNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    if (!isAllowed && !isSiteManagerFlow) { navigate('projects', {}); return; }
    if (!projectId) { navigate('projects', {}); return; }
    loadInstructions();
    generateReference();
    if (isSiteManagerFlow) loadInstrAttachments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (instructionId) {
      const ins = instructions.find(i => i.id === instructionId);
      setSelectedInstruction(ins || null);
      if (ins) {
        if (ins.date) setNotificationDate(ins.date);
        if (ins.description && !description) setDescription(ins.description);
      }
      loadLinkedDayworks(instructionId);
    } else {
      setSelectedInstruction(null);
      setLinkedDayworks([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instructionId, instructions]);

  async function loadInstructions() {
    const { data } = await supabase
      .from('instructions')
      .select('id, reference, type, description, date, location, reason_outside_scope, status')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });
    setInstructions(data || []);
    if (params?.instructionId && data) {
      const ins = data.find(i => i.id === params.instructionId);
      if (ins) {
        setSelectedInstruction(ins);
        if (ins.date) setNotificationDate(ins.date);
        if (ins.description) setDescription(ins.description);
      }
    }
  }

  async function loadLinkedDayworks(insId) {
    const { data } = await supabase
      .from('dayworks')
      .select('id, reference, date_of_works, status, daywork_labour(hours_calculated)')
      .eq('instruction_id', insId)
      .order('date_of_works');
    setLinkedDayworks(data || []);
  }

  async function loadInstrAttachments() {
    const { data } = await supabase
      .from('instruction_attachments')
      .select('*')
      .eq('instruction_id', params.instructionId);
    const atts = data || [];
    for (const att of atts) {
      const { data: signed } = await supabase.storage
        .from('instruction-attachments')
        .createSignedUrl(att.storage_path, 604800);
      att.signedUrl = signed?.signedUrl || null;
    }
    setInstrAttachments(atts);
  }

  async function generateReference() {
    const { data } = await supabase.from('variations').select('reference').eq('project_id', projectId);
    const { data: proj } = await supabase.from('projects').select('project_code, reference').eq('id', projectId).single();
    const nums = (data || []).map(r => { const m = r.reference?.match(/(\d+)$/); return m ? parseInt(m[1], 10) : 0; });
    const next = nums.length > 0 ? Math.max(...nums) + 1 : 1;
    const code = proj?.project_code || proj?.reference || 'PRJ';
    setGeneratedRef(`VAR-${code}-${String(next).padStart(3, '0')}`);
  }

  const proof = {
    instruction: !!selectedInstruction,
    entitlement: !!(selectedInstruction?.reason_outside_scope?.trim()),
    quantum: parseFloat(value) > 0,
    submission: false,
    application: false,
  };

  let daysElapsed = null;
  if (notificationDate) {
    daysElapsed = Math.floor((new Date() - new Date(notificationDate)) / 86400000);
  }

  async function handleSmRaise() {
    setError('');
    if (!title.trim()) { setError('Title is required.'); return; }
    if (!instructionId) { setError('No instruction linked.'); return; }
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const { data: proj } = await supabase.from('projects').select('id, reference, name, qs_user_id').eq('id', projectId).single();
      const { data: variation, error: insertErr } = await supabase
        .from('variations')
        .insert({
          project_id: projectId,
          instruction_id: instructionId,
          created_by: profile.id,
          raised_by: profile.id,
          raised_at: now,
          reference: generatedRef,
          title,
          description: description || null,
          scope_notes: scopeNotes || null,
          valuation_basis: valuationBasis,
          programme_impact_days: programmeImpact ? (parseInt(programmeImpactDays, 10) || 0) : 0,
          site_notes: siteNotes || null,
          status: 'qs_review',
          risk_level: 'medium',
          current_revision: 0,
          instruction_status: 'confirmed',
          entitlement_status: 'pending',
          quantum_status: 'pending',
          submission_status: 'pending',
          application_status: 'pending',
        })
        .select()
        .single();
      if (insertErr) throw insertErr;

      if (proj?.qs_user_id) {
        const { data: qsUser } = await supabase.from('profiles').select('email, full_name').eq('id', proj.qs_user_id).single();
        if (qsUser?.email) {
          const { subject, htmlBody } = varRaisedNotification(variation, proj, selectedInstruction, qsUser, profile, instrAttachments);
          await sendEmail({ to: qsUser.email, subject, htmlBody });
          await supabase.from('variations').update({ qs_notified_at: new Date().toISOString() }).eq('id', variation.id);
        }
      }

      if (attachments.length > 0) {
        window.__pendingUploads = window.__pendingUploads || {};
        window.__pendingUploads[variation.id] = attachments;
      }

      navigate('variation-detail', { variationId: variation.id });
    } catch (err) {
      setError(err.message || 'Failed to raise variation request.');
      setSaving(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  async function handleSave(submitNow) {
    setError('');
    if (!instructionId) { setError('Please link this variation to an instruction.'); return; }
    if (!title.trim()) { setError('Title is required.'); return; }
    if (!value || parseFloat(value) <= 0) { setError('A quoted value is required.'); return; }
    setSaving(true);
    try {
      const status = submitNow ? 'submitted' : 'draft';
      const now = new Date().toISOString();

      const proofChain = {
        instruction_status: proof.instruction ? 'confirmed' : 'pending',
        entitlement_status: proof.entitlement ? 'confirmed' : 'pending',
        quantum_status: proof.quantum ? 'confirmed' : 'pending',
        submission_status: submitNow ? 'confirmed' : 'pending',
        application_status: 'pending',
      };

      const { data: variation, error: insertErr } = await supabase
        .from('variations')
        .insert({
          project_id: projectId,
          instruction_id: instructionId || null,
          created_by: profile.id,
          reference: generatedRef,
          title,
          description,
          scope_notes: scopeNotes || null,
          valuation_basis: valuationBasis,
          value: parseFloat(value),
          status,
          risk_level: riskLevel,
          notification_date: notificationDate || null,
          programme_impact_days: programmeImpact ? (parseInt(programmeImpactDays, 10) || 0) : 0,
          vq_submitted_date: submitNow ? vqSubmittedDate : null,
          submitted_at: submitNow ? now : null,
          current_revision: submitNow ? 1 : 0,
          ...proofChain,
        })
        .select()
        .single();
      if (insertErr) throw insertErr;

      if (submitNow) {
        const { error: revErr } = await supabase
          .from('variation_revisions')
          .insert({
            variation_id: variation.id,
            revision_number: 1,
            submitted_by: profile.id,
            value: parseFloat(value),
            notes: notesForMc || null,
            status: 'submitted',
          });
        if (revErr) throw revErr;
      }

      if (instructionId && submitNow) {
        await supabase.from('instructions').update({ status: 'variation_submitted' }).eq('id', instructionId).eq('status', 'open');
      }

      // Hand off files — navigate immediately, never block on uploads
      if (attachments.length > 0) {
        window.__pendingUploads = window.__pendingUploads || {};
        window.__pendingUploads[variation.id] = attachments;
      }

      navigate('variation-detail', { variationId: variation.id });
      // Component unmounts here — intentionally not calling setSaving(false)
    } catch (err) {
      setError(err.message || 'Failed to save variation.');
      setSaving(false);
    }
  }

  if (isSiteManagerFlow) {
    return (
      <div style={S.pageContainer}>
        <div style={S.pageHeader}>
          <div>
            <button
              onClick={() => navigate('project-detail', { projectId })}
              style={{ ...S.btn, ...S.btnGhost, ...S.btnSm, marginBottom: '8px' }}
            >
              ← Back to Project
            </button>
            <h1 style={S.pageTitle}>Raise Variation Request</h1>
            <p style={{ color: C.textMuted, fontSize: '14px', margin: '4px 0 0 0' }}>
              Site Manager — request QS to price and submit a variation quotation
            </p>
          </div>
          <div style={{ background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '6px', padding: '12px 16px', maxWidth: '360px' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#92400e', textTransform: 'uppercase', marginBottom: '4px' }}>Reference</div>
            <div style={{ fontFamily: 'monospace', fontWeight: '700', color: C.accent, fontSize: '16px', letterSpacing: '0.06em' }}>{generatedRef || 'Generating...'}</div>
          </div>
        </div>

        {error && <div style={S.alertError}>{error}</div>}

        {/* 1 — Linked Instruction */}
        <div style={{ ...S.card, marginBottom: '20px' }}>
          <h2 style={{ ...S.sectionTitle, marginBottom: '14px', fontSize: '15px', borderBottom: `1px solid ${C.border}`, paddingBottom: '10px' }}>
            1 — Linked Instruction
          </h2>
          {selectedInstruction ? (
            <div style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: '6px', border: `1px solid ${C.border}` }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '6px' }}>
                <span style={{ fontWeight: '700', color: C.accent, fontFamily: 'monospace', fontSize: '15px' }}>{selectedInstruction.reference}</span>
                <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '3px', fontWeight: '700', fontSize: '11px' }}>{selectedInstruction.type}</span>
              </div>
              {selectedInstruction.description && (
                <div style={{ fontSize: '13px', color: C.text, marginBottom: '4px' }}>{selectedInstruction.description}</div>
              )}
              {selectedInstruction.date && (
                <div style={{ fontSize: '12px', color: C.textMuted }}>Date: {formatDate(selectedInstruction.date)}</div>
              )}
              <div style={{ marginTop: '12px', marginBottom: '2px', fontWeight: '700', fontSize: '13px', color: C.text }}>Instruction Attachments</div>
              {instrAttachments.length > 0 ? (
                <div style={{ marginTop: '8px' }}>
                  {instrAttachments.map(att => (
                    <div key={att.id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '20px', flexShrink: 0 }}>📄</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: '700', fontSize: '13px', color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.file_name}</div>
                        {att.file_size && <div style={{ fontSize: '12px', color: C.textMuted }}>{(att.file_size / 1024).toFixed(0)} KB</div>}
                      </div>
                      {att.signedUrl && (
                        <a
                          href={att.signedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ background: '#111827', color: '#fff', border: 'none', borderRadius: '4px', padding: '5px 12px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', textDecoration: 'none', flexShrink: 0 }}
                        >
                          Download
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '6px' }}>No attachments on this instruction</div>
              )}
            </div>
          ) : (
            <div style={{ color: C.textMuted, fontSize: '14px' }}>Loading instruction…</div>
          )}
        </div>

        {/* 2 — Description of Works */}
        <div style={{ ...S.card, marginBottom: '20px' }}>
          <h2 style={{ ...S.sectionTitle, marginBottom: '14px', fontSize: '15px', borderBottom: `1px solid ${C.border}`, paddingBottom: '10px' }}>
            2 — Description of Works
          </h2>
          <div style={S.formGroup}>
            <label style={S.label}>Title <span style={{ color: C.danger }}>*</span></label>
            <input style={S.input} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Additional partitions Level 1" />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Describe the additional works required</label>
            <textarea style={{ ...S.textarea, minHeight: '90px' }} value={description} onChange={e => setDescription(e.target.value)} placeholder="What work needs to be done and where…" />
          </div>
        </div>

        {/* 3 — Why This is a Variation */}
        <div style={{ ...S.card, marginBottom: '20px' }}>
          <h2 style={{ ...S.sectionTitle, marginBottom: '14px', fontSize: '15px', borderBottom: `1px solid ${C.border}`, paddingBottom: '10px' }}>
            3 — Why This is a Variation
          </h2>
          <div style={S.formGroup}>
            <label style={S.label}>Explain why this work is outside the original subcontract scope</label>
            <textarea style={{ ...S.textarea, minHeight: '80px' }} value={scopeNotes} onChange={e => setScopeNotes(e.target.value)} placeholder="e.g. Not shown on original drawings, instructed by MC after contract start…" />
          </div>
        </div>

        {/* 4 — Valuation Basis & Programme Impact */}
        <div style={{ ...S.card, marginBottom: '20px' }}>
          <h2 style={{ ...S.sectionTitle, marginBottom: '14px', fontSize: '15px', borderBottom: `1px solid ${C.border}`, paddingBottom: '10px' }}>
            4 — Valuation Basis &amp; Programme Impact
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '20px' }}>
            {VALUATION_OPTIONS.map(opt => (
              <label key={opt.value} style={{
                display: 'flex', alignItems: 'flex-start', gap: '8px',
                padding: '8px 12px',
                border: `2px solid ${valuationBasis === opt.value ? C.accent : C.border}`,
                borderRadius: '6px', cursor: 'pointer',
                background: valuationBasis === opt.value ? '#f5f5f5' : '#fff',
              }}>
                <input type="radio" name="valuation_basis" value={opt.value} checked={valuationBasis === opt.value} onChange={() => setValuationBasis(opt.value)} style={{ marginTop: '2px' }} />
                <div>
                  <div style={{ fontWeight: '600', fontSize: '13px', color: C.text }}>{opt.label}</div>
                  <div style={{ fontSize: '11px', color: C.textMuted }}>{opt.desc}</div>
                </div>
              </label>
            ))}
          </div>
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: '16px' }}>
            <label style={{ ...S.label, marginBottom: '8px' }}>Programme Impact</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
              {['No', 'Yes'].map(opt => (
                <button key={opt} type="button" onClick={() => setProgrammeImpact(opt === 'Yes')} style={{
                  ...S.btn, ...(programmeImpact === (opt === 'Yes') ? S.btnPrimary : S.btnGhost), ...S.btnSm,
                }}>{opt}</button>
              ))}
            </div>
            {programmeImpact && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px', color: C.textMuted }}>Estimated additional time:</span>
                <input type="number" min="0" style={{ ...S.input, width: '80px' }} value={programmeImpactDays} onChange={e => setProgrammeImpactDays(e.target.value)} />
                <span style={{ fontSize: '13px', color: C.textMuted }}>Man Days</span>
              </div>
            )}
          </div>
        </div>

        {/* 5 — Supporting Documents */}
        <div style={{ ...S.card, marginBottom: '24px' }}>
          <h2 style={{ ...S.sectionTitle, marginBottom: '8px', fontSize: '15px', borderBottom: `1px solid ${C.border}`, paddingBottom: '10px' }}>
            5 — Supporting Documents
          </h2>
          <p style={{ fontSize: '13px', color: C.textMuted, margin: '0 0 16px 0', lineHeight: '1.6' }}>
            The instruction attachments above are automatically included. Use the area below to add any additional documents, photos or drawings relevant to this variation request.
          </p>
          <div style={{ marginBottom: '16px' }}>
            <DropZone
              onFilesSelected={files => setAttachments(prev => {
                const existing = new Set(prev.map(f => f.name));
                return [...prev, ...files.filter(f => !existing.has(f.name))];
              })}
              files={attachments}
              onRemove={i => setAttachments(prev => prev.filter((_, j) => j !== i))}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              multiple={true}
              label="Photos, drawings, emails, instructions"
            />
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Notes for QS (optional)</label>
            <textarea
              style={{ ...S.textarea, minHeight: '70px' }}
              value={siteNotes}
              onChange={e => setSiteNotes(e.target.value)}
              placeholder="Any context that will help the QS price this variation…"
            />
          </div>
        </div>

        {error && (
          <div style={{ ...S.alertError, marginBottom: '12px' }}>{error}</div>
        )}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingBottom: '32px' }}>
          <button onClick={() => navigate('project-detail', { projectId })} style={{ ...S.btn, ...S.btnGhost }} disabled={saving}>
            Cancel
          </button>
          <button onClick={handleSmRaise} disabled={saving} style={{ ...S.btn, ...S.btnPrimary, ...S.btnLg, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Submitting…' : 'Submit Variation Request'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.pageContainer}>
      <div style={S.pageHeader}>
        <div>
          <button
            onClick={() => navigate('project-detail', { projectId })}
            style={{ ...S.btn, ...S.btnGhost, ...S.btnSm, marginBottom: '8px' }}
          >
            ← Back to Project
          </button>
          <h1 style={S.pageTitle}>New Variation</h1>
          <p style={{ color: C.textMuted, fontSize: '14px', margin: '4px 0 0 0' }}>
            QS Workflow — Variation Quotation management
          </p>
        </div>
      </div>

      {error && <div style={S.alertError}>{error}</div>}

      {/* 1 — Reference & Instruction */}
      <div style={{ ...S.card, marginBottom: '20px' }}>
        <h2 style={{ ...S.sectionTitle, marginBottom: '16px', fontSize: '15px', borderBottom: `1px solid ${C.border}`, paddingBottom: '10px' }}>
          1 — Reference &amp; Instruction
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div style={S.formGroup}>
            <label style={S.label}>VAR Reference</label>
            <div style={{ ...S.input, background: '#f9fafb', color: C.accent, fontFamily: 'monospace', fontWeight: '700', fontSize: '15px', letterSpacing: '0.06em' }}>
              {generatedRef || 'Generating...'}
            </div>
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>Notification Date (instruction received)</label>
            <input type="date" style={S.input} value={notificationDate} onChange={e => setNotificationDate(e.target.value)} />
          </div>
        </div>

        {daysElapsed !== null && daysElapsed > 14 && (
          <div style={{
            padding: '10px 14px', borderRadius: '6px', marginBottom: '14px',
            background: daysElapsed > 28 ? '#fef2f2' : '#fffbeb',
            border: `1px solid ${daysElapsed > 28 ? '#fecaca' : '#fde68a'}`,
            color: daysElapsed > 28 ? C.danger : C.warning,
            fontSize: '13px', fontWeight: '600',
          }}>
            {daysElapsed > 28 ? '🔴' : '⚠'} JCT notification required without delay. Instruction received {daysElapsed} days ago.
            {daysElapsed > 28 && ' Immediate action required — notification period may have expired.'}
          </div>
        )}

        <div style={S.formGroup}>
          <label style={S.label}>Linked Instruction <span style={{ color: C.danger }}>*</span></label>
          <select value={instructionId} onChange={e => setInstructionId(e.target.value)} style={S.select}>
            <option value="">— Select instruction —</option>
            {instructions.map(ins => (
              <option key={ins.id} value={ins.id}>
                {ins.reference} ({ins.type}) — {ins.description?.substring(0, 60) || ''}
              </option>
            ))}
          </select>
        </div>

        {selectedInstruction && (
          <div style={{ padding: '12px 14px', background: '#f8fafc', borderRadius: '6px', border: `1px solid ${C.border}`, fontSize: '13px' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: '700', color: C.accent, fontFamily: 'monospace' }}>{selectedInstruction.reference}</span>
              <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: '3px', fontWeight: '700', fontSize: '11px' }}>{selectedInstruction.type}</span>
              {selectedInstruction.reason_outside_scope
                ? <span style={{ background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: '3px', fontSize: '11px' }}>Entitlement documented</span>
                : <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '3px', fontSize: '11px' }}>⚠ No reason outside scope</span>}
            </div>
            {selectedInstruction.location && <div style={{ color: C.textMuted }}>Location: {selectedInstruction.location}</div>}
          </div>
        )}
      </div>

      {/* 2 — Scope of Works */}
      <div style={{ ...S.card, marginBottom: '20px' }}>
        <h2 style={{ ...S.sectionTitle, marginBottom: '16px', fontSize: '15px', borderBottom: `1px solid ${C.border}`, paddingBottom: '10px' }}>
          2 — Scope of Works
        </h2>

        <div style={S.formGroup}>
          <label style={S.label}>Title <span style={{ color: C.danger }}>*</span></label>
          <input style={S.input} value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Additional partitions Level 1" />
        </div>

        <div style={S.formGroup}>
          <label style={S.label}>Description of Works</label>
          <textarea style={{ ...S.textarea, minHeight: '90px' }} value={description} onChange={e => setDescription(e.target.value)} placeholder="Detailed description of the variation works..." />
        </div>

        <div style={S.formGroup}>
          <label style={S.label}>Scope Notes</label>
          <textarea style={{ ...S.textarea, minHeight: '70px' }} value={scopeNotes} onChange={e => setScopeNotes(e.target.value)} placeholder="What is specifically included and excluded from this variation..." />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div style={S.formGroup}>
            <label style={S.label}>Valuation Basis</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {VALUATION_OPTIONS.map(opt => (
                <label key={opt.value} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '8px',
                  padding: '8px 12px',
                  border: `2px solid ${valuationBasis === opt.value ? C.accent : C.border}`,
                  borderRadius: '6px', cursor: 'pointer',
                  background: valuationBasis === opt.value ? '#f5f5f5' : '#fff',
                }}>
                  <input type="radio" name="valuation_basis" value={opt.value} checked={valuationBasis === opt.value} onChange={() => setValuationBasis(opt.value)} style={{ marginTop: '2px' }} />
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '13px', color: C.text }}>{opt.label}</div>
                    <div style={{ fontSize: '11px', color: C.textMuted }}>{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div style={S.formGroup}>
              <label style={S.label}>Risk Level</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {RISK_OPTIONS.map(opt => (
                  <div key={opt.value} onClick={() => setRiskLevel(opt.value)} style={{
                    padding: '10px 14px', borderRadius: '8px', cursor: 'pointer',
                    border: `2px solid ${riskLevel === opt.value ? opt.color : C.border}`,
                    background: riskLevel === opt.value ? opt.bg : '#fff',
                    transition: 'all 0.1s',
                  }}>
                    <div style={{ fontWeight: '700', fontSize: '13px', color: riskLevel === opt.value ? opt.color : C.text }}>{opt.label}</div>
                    <div style={{ fontSize: '11px', color: C.textMuted }}>{opt.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ ...S.formGroup, marginTop: '12px' }}>
              <label style={S.label}>Programme Impact</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                {['No', 'Yes'].map(opt => (
                  <button key={opt} type="button" onClick={() => setProgrammeImpact(opt === 'Yes')} style={{
                    ...S.btn, ...(programmeImpact === (opt === 'Yes') ? S.btnPrimary : S.btnGhost), ...S.btnSm,
                  }}>{opt}</button>
                ))}
              </div>
              {programmeImpact && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '13px', color: C.textMuted }}>Additional time required:</span>
                  <input type="number" min="0" style={{ ...S.input, width: '80px' }} value={programmeImpactDays} onChange={e => setProgrammeImpactDays(e.target.value)} />
                  <span style={{ fontSize: '13px', color: C.textMuted }}>Man Days</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 3 — Linked Dayworks (conditional) */}
      {linkedDayworks.length > 0 && (
        <div style={{ ...S.card, marginBottom: '20px' }}>
          <h2 style={{ ...S.sectionTitle, marginBottom: '12px', fontSize: '15px', borderBottom: `1px solid ${C.border}`, paddingBottom: '10px' }}>
            3 — Linked Dayworks
          </h2>
          <p style={{ fontSize: '13px', color: C.textMuted, marginBottom: '12px' }}>These daywork costs may inform the variation value.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {linkedDayworks.map(dw => {
              const hrs = (dw.daywork_labour || []).reduce((s, l) => s + (parseFloat(l.hours_calculated) || 0), 0);
              const st = getStatusBadgeStyle(dw.status);
              return (
                <div key={dw.id} style={{ padding: '8px 12px', background: '#f9fafb', borderRadius: '6px', border: `1px solid ${C.border}`, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontWeight: '700', color: C.accent, fontFamily: 'monospace' }}>{dw.reference}</span>
                  <span style={{ color: C.textMuted }}>{formatDate(dw.date_of_works)}</span>
                  {hrs > 0 && <span style={{ color: C.success, fontWeight: '600' }}>{hrs.toFixed(1)}h</span>}
                  <span style={{ ...S.badge, ...st }}>{dw.status}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Value & Submission */}
      <div style={{ ...S.card, marginBottom: '20px' }}>
        <h2 style={{ ...S.sectionTitle, marginBottom: '16px', fontSize: '15px', borderBottom: `1px solid ${C.border}`, paddingBottom: '10px' }}>
          {linkedDayworks.length > 0 ? '4' : '3'} — Value &amp; Submission
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div style={S.formGroup}>
            <label style={S.label}>Quoted Value (£) <span style={{ color: C.danger }}>*</span></label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: C.textMuted, fontWeight: '600' }}>£</span>
              <input
                type="number" step="0.01" style={{ ...S.input, paddingLeft: '24px' }}
                value={value} onChange={e => setValue(e.target.value)} placeholder="0.00"
              />
            </div>
            {value && parseFloat(value) > 0 && (
              <div style={{ fontSize: '13px', color: C.success, fontWeight: '600', marginTop: '4px' }}>{formatCurrency(parseFloat(value))}</div>
            )}
          </div>
          <div style={S.formGroup}>
            <label style={S.label}>VQ Submission Date</label>
            <input type="date" style={S.input} value={vqSubmittedDate} onChange={e => setVqSubmittedDate(e.target.value)} />
          </div>
        </div>

        <div style={S.formGroup}>
          <label style={S.label}>Notes to MC / Covering Letter Text</label>
          <textarea style={{ ...S.textarea, minHeight: '100px', fontSize: '13px' }} value={notesForMc} onChange={e => setNotesForMc(e.target.value)} />
        </div>

        <div style={{ marginTop: '8px' }}>
          <label style={{ ...S.label, marginBottom: '8px' }}>VQ Documents (optional)</label>
          <DropZone
            onFilesSelected={files => setAttachments(prev => {
              const existing = new Set(prev.map(f => f.name));
              return [...prev, ...files.filter(f => !existing.has(f.name))];
            })}
            files={attachments}
            onRemove={i => setAttachments(prev => prev.filter((_, j) => j !== i))}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.msg,.eml,.jpg,.jpeg,.png"
            multiple={true}
            label="VQ documents and drawings"
          />
          <p style={{ fontSize: '12px', color: C.textMuted, margin: '10px 0 0 0' }}>
            Additional documents can also be added from the variation detail page after saving.
          </p>
        </div>
      </div>

      {/* Proof Chain */}
      <div style={{ ...S.card, marginBottom: '24px' }}>
        <h2 style={{ ...S.sectionTitle, marginBottom: '12px', fontSize: '15px', borderBottom: `1px solid ${C.border}`, paddingBottom: '10px' }}>
          {linkedDayworks.length > 0 ? '5' : '4'} — Proof Chain
          <span style={{ fontSize: '12px', color: C.textMuted, fontWeight: '400', marginLeft: '8px' }}>Auto-populated</span>
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[
            { key: 'instruction', label: 'Instruction', desc: selectedInstruction ? `${selectedInstruction.reference} linked` : 'No instruction linked', ok: proof.instruction },
            { key: 'entitlement', label: 'Entitlement', desc: proof.entitlement ? 'Reason outside scope documented on instruction' : 'Missing — add reason_outside_scope to instruction', ok: proof.entitlement },
            { key: 'quantum', label: 'Quantum', desc: proof.quantum ? `Value: ${formatCurrency(parseFloat(value))}` : 'Enter a value for this variation', ok: proof.quantum },
            { key: 'submission', label: 'Submission', desc: 'Confirmed when submitted to MC', ok: false },
            { key: 'application', label: 'Application', desc: 'Confirmed when PayApp number set', ok: false },
          ].map(step => (
            <div key={step.key} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderRadius: '6px', background: step.ok ? '#f0fdf4' : '#fafafa', border: `1px solid ${step.ok ? '#bbf7d0' : C.border}` }}>
              <ProofDot status={step.ok ? 'confirmed' : 'pending'} />
              <div>
                <div style={{ fontWeight: '600', fontSize: '13px', color: C.text }}>{step.label}</div>
                <div style={{ fontSize: '12px', color: C.textMuted }}>{step.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap', paddingBottom: '32px' }}>
        <button onClick={() => navigate('project-detail', { projectId })} style={{ ...S.btn, ...S.btnGhost }} disabled={saving}>
          Cancel
        </button>
        <button onClick={() => handleSave(false)} disabled={saving} style={{ ...S.btn, ...S.btnOutline, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Saving...' : 'Save Draft'}
        </button>
        <button onClick={() => handleSave(true)} disabled={saving} style={{ ...S.btn, ...S.btnPrimary, ...S.btnLg, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Submitting...' : 'Save & Submit to MC'}
        </button>
      </div>
    </div>
  );
}
